import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, Validators } from '@angular/forms';
import { NotificationService } from '../../../core/services';
import { IdentityService } from '../../../core/services/auth/identity.service';
import { AddEditFormGeneric } from '../../../ui/generics';
import { StoresService } from '../core/services/StoresService';
import { StoresServiceErrorResolverService } from '../core/services/error-resolvers/stores-service-error-resolver.service';
import { IStore } from '../core/interfaces/IStore';
import { NzModalService } from 'ng-zorro-antd/modal';

@Component({
    selector: 'app-add-edit-form',
    templateUrl: './add-edit-form.component.html',
    styleUrls: ['./add-edit-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AddEditFormComponent extends AddEditFormGeneric<IStore> implements OnInit {
  isAdmin = false;

  constructor(
    protected fb: FormBuilder,
    protected suppliersService: StoresService,
    protected notifyService: NotificationService,
    protected errorResolver: StoresServiceErrorResolverService,
    private identity: IdentityService,
    private modal: NzModalService,
  ) {
    super(fb, suppliersService, notifyService, errorResolver);
    this.isAdmin = this.identity.getCurrentIdentity()?.role === 'Administrator';
    this.form = this._fb.group({
      name: ['', Validators.required],
      shortCode: ['', [Validators.required, Validators.maxLength(32)]],
      address: ['', Validators.required],
      flourStoreIds: this._fb.array<FormControl<string>>([]),
      flourStoreKey: [''],
      isFlourStore: [false],
      isMainStore: [false, Validators.required],
      isBbmStore: [{ value: false, disabled: !this.isAdmin }],
      isActive: [true, Validators.required],
    });
  }

  get isNewStore(): boolean {
    return !this.editObject;
  }

  get mainStoreHint(): string | null {
    if (this.isNewStore) {
      return 'Ein neues Lager kann nicht das Haupt-Lagerhaus sein.';
    }
    if (this.editObject?.isMainStore) {
      return 'Es muss immer genau ein Haupt-Lagerhaus geben. Die Markierung kann hier nicht entfernt werden.';
    }
    return null;
  }

  get flourStoreIds(): FormArray<FormControl<string>> {
    return this.form.get('flourStoreIds') as FormArray<FormControl<string>>;
  }

  addFlourStoreId(): void {
    this.flourStoreIds.push(new FormControl('', { nonNullable: true }));
  }

  removeFlourStoreId(index: number): void {
    this.flourStoreIds.removeAt(index);
  }

  override resetSubmitState(resetValues = false): void {
    super.resetSubmitState(resetValues);
    if (resetValues) {
      this.setFlourStoreIds(undefined);
    }
    this.applyStoreLocks();
  }

  protected afterLoadForm(): void {
    this.applyStoreLocks();
    this.setFlourStoreIds(this.editObject?.pointOfSales);
  }

  saveEdit(): void {
    const raw = this.form.getRawValue();
    const flagChanged = !!this.editObject?.isBbmStore !== !!raw.isBbmStore;
    if (!flagChanged) {
      this.persist(raw);
      return;
    }

    this.confirmBbmChange(!!raw.isBbmStore, () => this.persist(raw));
  }

  saveNew(): void {
    const raw = this.form.getRawValue();
    if (raw.isBbmStore) {
      this.confirmBbmChange(true, () => this.persistCreate(raw));
      return;
    }
    this.persistCreate(raw);
  }

  private persist(raw: IStore & { flourStoreIds?: string[] }): void {
    const object = this.toStore(raw, false);
    this._formService.update(object, this.editObject.id).subscribe(() => {
      this.suppliersService.refreshSimple();
      this._notifyService.pushSuccess('Erfolgreich', 'Element wurde korrekt aktualisiert.');
      this.formFinished.emit(true);
    }, err => {
      this.parseError(err);
      this.formFinished.emit(false);
    });
  }

  private persistCreate(raw: IStore & { flourStoreIds?: string[] }): void {
    const object = this.toStore(raw, true);
    this._formService.create(object).subscribe(() => {
      this.suppliersService.refreshSimple();
      this._notifyService.pushSuccess('Erfolgreich', 'Element wurde korrekt erstellt');
      this.formFinished.emit(true);
    }, err => {
      this.parseError(err);
      this.formFinished.emit(false);
    });
  }

  private confirmBbmChange(enabled: boolean, onOk: () => void): void {
    this.modal.confirm({
      nzTitle: 'BBM-Bestandsberechnung ändern?',
      nzContent: enabled
        ? 'Dieses Lager wird ab sofort in den live berechneten BBM-Beständen mitgezählt. Historische Bestände, abgeschlossene Umlagerungen und gebuchte Mengen bleiben unverändert.'
        : 'Dieses Lager wird ab sofort nicht mehr in den live berechneten BBM-Beständen mitgezählt. Historische Bestände, abgeschlossene Umlagerungen und gebuchte Mengen bleiben unverändert.',
      nzOkText: 'Speichern',
      nzCancelText: 'Abbrechen',
      nzOnOk: () => onOk(),
      nzOnCancel: () => this.formFinished.emit(false),
    });
  }

  private applyStoreLocks(): void {
    const mainStore = this.form.get('isMainStore');
    if (this.isNewStore || this.editObject?.isMainStore) {
      if (this.isNewStore) {
        mainStore.setValue(false, { emitEvent: false });
      }
      mainStore.disable({ emitEvent: false });
    } else {
      mainStore.enable({ emitEvent: false });
    }

    const bbmStore = this.form.get('isBbmStore');
    if (this.isAdmin) {
      bbmStore.enable({ emitEvent: false });
    } else {
      bbmStore.disable({ emitEvent: false });
    }
  }

  private toStore(raw: IStore & { flourStoreIds?: string[] }, forceNotMain: boolean): IStore {
    const { flourStoreIds, ...rest } = raw;
    return {
      ...(forceNotMain ? {} : this.editObject),
      ...rest,
      isMainStore: forceNotMain ? false : rest.isMainStore,
      pointOfSales: this.joinFlourStoreIds(),
    };
  }

  private setFlourStoreIds(value: string | undefined): void {
    this.flourStoreIds.clear();
    this.splitFlourStoreIds(value).forEach(id => {
      this.flourStoreIds.push(new FormControl(id, { nonNullable: true }));
    });
  }

  private joinFlourStoreIds(): string {
    const ids = this.flourStoreIds.controls
      .map(control => control.value.trim())
      .filter(id => id.length > 0);
    return [...new Set(ids)].join(',');
  }

  private splitFlourStoreIds(value: string | undefined): string[] {
    return (value || '')
      .split(',')
      .map(id => id.trim())
      .filter(id => id.length > 0);
  }
}
